/**
 * The org reminder/SLA form, and the same sanitizing the server applies.
 *
 * The backend already schedules nudges; this is only the settings page that was
 * missing. Offsets are treated as untrusted here for the same reason they are
 * on the server: a stray 0 would fire at the due instant, and a paste of thirty
 * values would become thirty jobs per task.
 */

export const DEFAULT_REMIND_BEFORE_MINUTES = [1440, 60];
export const DEFAULT_BREACH_GRACE_MINUTES = 60;
export const MAX_REMINDER_OFFSET_MINUTES = 60 * 24 * 30;
export const MAX_REMINDER_OFFSETS = 4;

export type ActionReminderPolicy = {
  groupGuid: string;
  remindBeforeMinutes: number[];
  breachEscalateAfterMinutes: number;
  breachEscalateTo: string | null;
  enabled: boolean;
};

export type ReminderPolicyDraft = {
  enabled: boolean;
  /**
   * Free-text minutes, one per line, so a bad row is visible rather than
   * dropped silently.
   */
  remindBeforeText: string;
  breachEscalateAfterMinutes: string;
  breachEscalateTo: string;
};

export function policyToDraft(
  policy: ActionReminderPolicy
): ReminderPolicyDraft {
  return {
    enabled: policy.enabled,
    remindBeforeText: policy.remindBeforeMinutes.join('\n'),
    breachEscalateAfterMinutes: String(policy.breachEscalateAfterMinutes),
    breachEscalateTo: policy.breachEscalateTo ?? '',
  };
}

export function sanitizeRemindBeforeMinutes(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const minutes = value
    .map((entry) => (typeof entry === 'number' ? entry : Number(entry)))
    .filter(
      (entry) =>
        Number.isFinite(entry) &&
        Number.isInteger(entry) &&
        entry > 0 &&
        entry <= MAX_REMINDER_OFFSET_MINUTES
    );
  return [...new Set(minutes)]
    .sort((a, b) => b - a)
    .slice(0, MAX_REMINDER_OFFSETS);
}

export function sanitizeBreachGraceMinutes(value: unknown): number {
  const minutes = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(minutes) || !Number.isInteger(minutes) || minutes < 0) {
    return DEFAULT_BREACH_GRACE_MINUTES;
  }
  return Math.min(minutes, MAX_REMINDER_OFFSET_MINUTES);
}

export function parseOffsetLines(text: string): number[] {
  return sanitizeRemindBeforeMinutes(
    text
      .split(/\n|,/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => Number(line))
  );
}

export function draftToPolicyBody(draft: ReminderPolicyDraft): {
  enabled: boolean;
  remindBeforeMinutes: number[];
  breachEscalateAfterMinutes: number;
  breachEscalateTo: string | null;
} {
  return {
    enabled: draft.enabled,
    remindBeforeMinutes: parseOffsetLines(draft.remindBeforeText),
    breachEscalateAfterMinutes: sanitizeBreachGraceMinutes(
      draft.breachEscalateAfterMinutes
    ),
    breachEscalateTo: draft.breachEscalateTo.trim() || null,
  };
}

export function describeOffset(minutes: number): string {
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? '1 day before' : `${days} days before`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 hour before' : `${hours} hours before`;
  }
  return `${minutes} minutes before`;
}

export function describeReminderSchedule(minutes: number[]): string {
  if (minutes.length === 0) return 'No reminders scheduled.';
  return minutes.map(describeOffset).join(', ');
}
